/**
 * Démarrer une affaire — deux portes d'entrée, un accordéon.
 *
 * **Depuis OneDrive**, la porte mise en avant (ouverte par défaut) : on désigne
 * le dossier racine où l'affaire vivra, on la nomme, on la crée. Le dossier est
 * gardé avec l'affaire ; le rattachement automatique de ses pièces viendra.
 * **Manuellement**, la voie sans OneDrive : on saisit la référence, l'affaire
 * naît, et les sources s'ajoutent ensuite depuis son onglet Documents.
 *
 * **Une référence déjà prise est refusée** côté portail (409) : plutôt que de
 * rendre l'affaire existante en silence, il la nomme, et l'écran propose de
 * l'ouvrir.
 */
import { useState } from "react";

import { api, ErreurApi } from "../api/client";
import type { DossierRacine } from "../api/types";
import { Avis } from "../composants/avis";
import { Lien, naviguer } from "../routeur";
import { SelecteurDossier } from "./affaire/SelecteurDossier";

type Voie = "onedrive" | "manuel";

/** Le chevron d'un volet, tourné vers le bas quand il est ouvert. */
function Chevron({ ouvert }: { ouvert: boolean }) {
  return (
    <svg className="accordeon__chevron" width="16" height="16"
         viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" aria-hidden="true">
      <path d={ouvert ? "m6 9 6 6 6-6" : "m9 6 6 6-6 6"} />
    </svg>
  );
}

export function NouvelleAffaire() {
  // OneDrive ouvert par défaut : c'est la voie qu'on veut mettre en avant.
  const [voie, poserVoie] = useState<Voie>("onedrive");
  const [reference, poserReference] = useState("");
  const [label, poserLabel] = useState("");
  const [envoi, poserEnvoi] = useState(false);
  const [erreur, poserErreur] = useState<string>("");
  // Sur une référence déjà prise, l'affaire qui l'occupe : l'écran propose de
  // l'ouvrir au lieu de laisser deviner où est passé le numéro.
  const [existante, poserExistante] = useState<string | null>(null);
  // Le dossier racine choisi dans OneDrive, et l'explorateur qui le désigne.
  const [dossierRacine, poserDossierRacine] = useState<DossierRacine | null>(null);
  const [navigateur, poserNavigateur] = useState(false);

  async function envoyer(racine: DossierRacine | null) {
    poserEnvoi(true);
    poserErreur("");
    poserExistante(null);
    try {
      const issue = await api.ecrire<{ affaire_id?: string }>(
        "/api/v1/affaires",
        { reference: reference.trim(), label: label.trim() || null, racine },
      );
      naviguer(issue.affaire_id ? `/affaires/${issue.affaire_id}` : "/affaires");
    } catch (exc) {
      if (exc instanceof ErreurApi) {
        poserErreur(exc.message);
        // Le corps du 409 porte l'affaire en place : on garde son identifiant
        // pour offrir de l'ouvrir.
        if (exc.probleme.affaire_id) poserExistante(exc.probleme.affaire_id);
      } else {
        poserErreur("La création n'a pas abouti. Réessayez.");
      }
    } finally {
      poserEnvoi(false);
    }
  }

  // Un seul avis d'erreur, rendu dans le volet ouvert — ils sont exclusifs.
  const avisErreur = erreur ? (
    <Avis ton="erreur" cle={erreur}>
      <div className="pile pile--serree">
        <span>{erreur}</span>
        {existante && (
          <Lien vers={`/affaires/${existante}`} className="bouton bouton--discret">
            Ouvrir l'affaire existante
          </Lien>
        )}
      </div>
    </Avis>
  ) : null;

  return (
    <div className="contenu pile">
      <div className="pile pile--serree">
        <h1>Démarrer une affaire</h1>
        <span className="discret">
          Son espace technique est préparé en arrière-plan ; elle devient
          utilisable dès qu'il est prêt.
        </span>
      </div>

      <div className="accordeon">
        <section className="accordeon__volet">
          <button type="button" className="accordeon__tete"
                  aria-expanded={voie === "onedrive"}
                  aria-controls="volet-onedrive"
                  onClick={() => poserVoie("onedrive")}>
            <span className="accordeon__titre">
              <strong>Créer depuis OneDrive</strong>
              <span className="discret">
                Partir du dossier lié où l'affaire vivra.
              </span>
            </span>
            <Chevron ouvert={voie === "onedrive"} />
          </button>
          {voie === "onedrive" && (
            <div id="volet-onedrive" role="region"
                 aria-label="Créer depuis OneDrive"
                 className="accordeon__panneau">
              <ol className="etapes">
                <li className="etape">
                  <span className="etape__rang" aria-hidden="true">1</span>
                  <div className="pile pile--serree" style={{ flex: 1, minWidth: 0 }}>
                    <strong>Choisir le dossier racine</strong>
                    <span className="discret">
                      Le dossier OneDrive où vit l'affaire ; la plateforme y
                      puisera ses pièces.
                    </span>
                    {dossierRacine ? (
                      <div className="rangee dossier-choisi">
                        <span aria-hidden="true">📁</span>
                        <span className="pile pile--serree"
                              style={{ flex: 1, minWidth: 0 }}>
                          <strong className="tronque">{dossierRacine.nom}</strong>
                          <span className="discret tronque mono">
                            {dossierRacine.chemin}
                          </span>
                        </span>
                        <button type="button" className="bouton bouton--discret"
                                onClick={() => poserNavigateur(true)}>
                          Changer
                        </button>
                      </div>
                    ) : (
                      <div>
                        <button type="button" className="bouton bouton--primaire"
                                onClick={() => poserNavigateur(true)}>
                          Choisir le dossier racine
                        </button>
                      </div>
                    )}
                  </div>
                </li>

                <li className="etape">
                  <span className="etape__rang" aria-hidden="true">2</span>
                  <div className="pile pile--serree" style={{ flex: 1, minWidth: 0 }}>
                    <strong>Nommer l'affaire, et la créer</strong>
                    <span className="discret">
                      La référence du cabinet ; l'intitulé reprend le nom du
                      dossier, à corriger au besoin.
                    </span>
                    <div className="pile" style={{ maxWidth: 620, marginTop: "var(--mo-space-1)" }}>
                      {avisErreur}

                      <div className="champ">
                        <label htmlFor="reference-od">Référence affaire *</label>
                        <input
                          id="reference-od"
                          value={reference}
                          onChange={(e) => poserReference(e.target.value)}
                          maxLength={64}
                          placeholder="30429"
                        />
                      </div>

                      <div className="champ">
                        <label htmlFor="label-od">Intitulé</label>
                        <input
                          id="label-od"
                          value={label}
                          onChange={(e) => poserLabel(e.target.value)}
                          maxLength={200}
                          placeholder="Réhabilitation du groupe scolaire Jean-Moulin"
                        />
                      </div>

                      <div className="rangee">
                        <button
                          type="button"
                          className="bouton bouton--primaire"
                          disabled={envoi || !reference.trim() || !dossierRacine}
                          onClick={() => void envoyer(dossierRacine)}
                        >
                          {envoi ? "Création…" : "Créer l'affaire"}
                        </button>
                        <Lien vers="/affaires" className="bouton bouton--discret">
                          Annuler
                        </Lien>
                      </div>
                    </div>
                  </div>
                </li>
              </ol>
              <p className="discret">
                Le dossier racine est mémorisé avec l'affaire. Le rattachement
                automatique de ses pièces viendra ; d'ici là, ajoutez-les depuis
                l'onglet Documents.
              </p>
            </div>
          )}
        </section>

        <section className="accordeon__volet">
          <button type="button" className="accordeon__tete"
                  aria-expanded={voie === "manuel"}
                  aria-controls="volet-manuel"
                  onClick={() => poserVoie("manuel")}>
            <span className="accordeon__titre">
              <strong>Créer manuellement</strong>
              <span className="discret">
                Saisir la référence ; les sources s'ajoutent ensuite.
              </span>
            </span>
            <Chevron ouvert={voie === "manuel"} />
          </button>
          {voie === "manuel" && (
            <div id="volet-manuel" role="region" aria-label="Créer manuellement"
                 className="accordeon__panneau">
              <form
                className="pile"
                style={{ maxWidth: 620 }}
                onSubmit={(evenement) => {
                  evenement.preventDefault();
                  void envoyer(null);
                }}
              >
                {avisErreur}

                <div className="champ">
                  <label htmlFor="reference">Référence affaire *</label>
                  <input
                    id="reference"
                    value={reference}
                    onChange={(e) => poserReference(e.target.value)}
                    required
                    maxLength={64}
                    autoFocus
                    placeholder="30429"
                  />
                  <span className="champ__aide">
                    Le numéro que le cabinet emploie. Il n'a besoin d'être unique
                    que chez vous : deux cabinets peuvent chiffrer le même.
                  </span>
                </div>

                <div className="champ">
                  <label htmlFor="label">Intitulé</label>
                  <input
                    id="label"
                    value={label}
                    onChange={(e) => poserLabel(e.target.value)}
                    maxLength={200}
                    placeholder="Réhabilitation du groupe scolaire Jean-Moulin"
                  />
                </div>

                <div className="avis avis--info">
                  L'identifiant interne et la clé d'espace de travail sont
                  attribués automatiquement, et ne se modifient plus ensuite.
                </div>

                <div className="rangee">
                  <button
                    type="submit"
                    className="bouton bouton--primaire"
                    disabled={envoi || !reference.trim()}
                  >
                    {envoi ? "Création…" : "Créer l'affaire"}
                  </button>
                  <Lien vers="/affaires" className="bouton bouton--discret">
                    Annuler
                  </Lien>
                </div>
              </form>
            </div>
          )}
        </section>
      </div>

      {navigateur && (
        <SelecteurDossier
          fermer={() => poserNavigateur(false)}
          onChoisir={(dossier) => {
            poserDossierRacine(dossier);
            // L'intitulé, s'il est encore vide, reprend le nom du dossier : le
            // métreur le corrige s'il veut, mais part rarement de rien.
            if (!label.trim()) poserLabel(dossier.nom);
            poserNavigateur(false);
          }}
        />
      )}
    </div>
  );
}
